import { Grid } from '../../../modules/design-system';
import { HeaderPillButton } from './HeaderPillButton';

export interface MyPacksPillProps {
  onClick?: () => void;
  label?: string;
  /** Cards in the Vault — shown as a badge on the pill's corner; 0 hides it. */
  count?: number;
}

/** Above this the badge reads "99+" so it stays a small circle. */
const MAX_BADGE_COUNT = 99;

/**
 * Sleek MyPacks pill — forked from modern (Figma `543:6354` / `425:1048`).
 * Labelled "Vault" (partner feedback 2026-10-01: the page is the Vault).
 *
 * Same glass surface on every breakpoint, shared with {@link LoginPill}
 * through {@link HeaderPillButton}. Below 600px the label is swapped for
 * Betr's grid glyph (partner Figma 2026-10-08), icon-only, so the pill stays
 * compact next to `BalancePill`; the label returns at >= 600px. The corner badge counts the Vault's cards
 * and stays while there are any (partner: "persistent"); its fill and digit
 * colours are Design Constructor knobs.
 */
export function MyPacksPill({ onClick, label = 'Vault', count = 0 }: MyPacksPillProps) {
  const hasBadge = count > 0;
  const badgeText = count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count);
  return (
    <HeaderPillButton
      onClick={onClick}
      ariaLabel={hasBadge ? `${label}, ${count} ${count === 1 ? 'card' : 'cards'}` : label}
      className="relative w-10 @[600px]:w-auto @[600px]:px-5"
    >
      <Grid size={16} className="@[600px]:hidden" />
      <span className="hidden @[600px]:inline">{label}</span>
      {hasBadge && (
        <span
          aria-hidden
          data-testid="my-packs-indicator"
          className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none tabular-nums bg-[var(--co-MyPacksPill-indicatorColor,#FF3B30)] text-[var(--co-MyPacksPill-indicatorText,#FFFFFF)]"
        >
          {badgeText}
        </span>
      )}
    </HeaderPillButton>
  );
}
